import { useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function normalizeProviderId(providerId) {
  if (!providerId || providerId === 'null' || providerId === 'undefined') return null;
  return providerId;
}

async function getExistingSubscription(registration) {
  try {
    return await registration.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
}

function arrayBufferEquals(left, right) {
  if (!left || !right || left.byteLength !== right.byteLength) return false;

  const leftView = new Uint8Array(left);
  const rightView = new Uint8Array(right);
  for (let index = 0; index < leftView.length; index += 1) {
    if (leftView[index] !== rightView[index]) return false;
  }
  return true;
}

function subscriptionMatchesPublicKey(subscription, applicationServerKey) {
  const existingKey = subscription?.options?.applicationServerKey;
  if (!existingKey) return true;
  return arrayBufferEquals(existingKey, applicationServerKey);
}

export default function usePushNotifications({ enabled, role, userId, providerId }) {
  useEffect(() => {
    if (!enabled || !userId) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return;
    }

    let cancelled = false;

    const subscribe = async () => {
      try {
        const { data } = await axios.get(`${API}/api/push/vapid-public-key`);
        const publicKey = data?.publicKey;
        if (!publicKey || cancelled) return;

        const registration = await navigator.serviceWorker.register('/sw.js');
        if (cancelled) return;

        let permission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted' || cancelled) return;

        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        let subscription = await getExistingSubscription(registration);
        if (subscription && !subscriptionMatchesPublicKey(subscription, applicationServerKey)) {
          await subscription.unsubscribe();
          subscription = null;
        }

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        }

        await axios.post(`${API}/api/push/subscribe`, {
          user_id: userId,
          role,
          provider_id: normalizeProviderId(providerId),
          subscription: subscription.toJSON(),
        });
      } catch (error) {
        console.warn('Push notification setup failed:', error?.message || error);
      }
    };

    subscribe();

    return () => {
      cancelled = true;
    };
  }, [enabled, providerId, role, userId]);
}
